import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { Draggable } from "gsap/Draggable"
import {
  MouseEvent,
  MouseEventHandler,
  RefObject,
  TouchEventHandler,
  useCallback,
  useEffect,
  useRef
} from "react"
import { hapticFeedback } from "@/helpers/hapticFeedback"
import { ICardOwner, ICardStatus } from "@/pages/Game/ui"

interface IUseCardInteractionsParams {
  owner?: ICardOwner
  status?: ICardStatus
  isInteractive?: boolean
  isAvailableToUse?: boolean
  refIsMyTurn?: RefObject<boolean>
  refIsMatchActive?: RefObject<boolean>
  onClick?: MouseEventHandler<HTMLDivElement>
  onClose?: () => void
  onActivateCard?: () => void
}

export const useCardInteractions = ({
  owner,
  status,
  isInteractive = true,
  isAvailableToUse = false,
  refIsMyTurn,
  refIsMatchActive,
  onClick,
  onClose,
  onActivateCard
}: IUseCardInteractionsParams) => {
  const refCard = useRef<HTMLDivElement | null>(null)
  const refContent = useRef<HTMLDivElement | null>(null)

  const refIsAvailableToUse = useRef<boolean>(false)
  const refIsDraggable = useRef<boolean>(false)

  const refOnClick = useRef(onClick)
  const refOnClose = useRef(onClose)
  const refOnActivateCard = useRef(onActivateCard)

  useEffect(() => {
    refOnClick.current = onClick
  }, [onClick])

  useEffect(() => {
    refOnClose.current = onClose
  }, [onClose])

  useEffect(() => {
    refOnActivateCard.current = onActivateCard
  }, [onActivateCard])

  useEffect(() => {
    refIsAvailableToUse.current = isAvailableToUse
  }, [isAvailableToUse])

  const onCardClick: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
    if (refIsDraggable.current) {
      refIsDraggable.current = false
      return
    }

    refOnClick.current?.(e)
  }, [])

  const onCardTouchEnd: TouchEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      onCardClick(e as unknown as MouseEvent<HTMLDivElement>)
    },
    [onCardClick]
  )

  useGSAP(() => {
    switch (status) {
      case "gold-active":
      case "deck-user":
      case "deck-active":
        gsap.from(refContent.current, {
          transform: "rotateY(180deg)",
          duration: 0.3,
          ease: "power2.inOut"
        })
        break
      case "user-deck":
        gsap.timeline().fromTo(
          refContent.current,
          {
            transform: "rotateY(0deg)"
          },
          {
            transform: "rotateY(180deg)",
            duration: 0.3,
            ease: "power2.inOut"
          }
        )
        break
      case "user-active":
        break
      case "user-opponent":
        gsap.to(refContent.current, {
          rotate: "180deg",
          transform: "rotateY(180deg)",
          duration: 0.3,
          ease: "power2.inOut"
        })
        break
      case "deck-opponent":
        gsap.from(refContent.current, {
          rotate: "180deg",
          duration: 0.3,
          ease: "power2.inOut"
        })
        break
      case "opponent-deck":
        gsap.from(refContent.current, {
          rotate: "180deg",
          duration: 0.5,
          ease: "power2.out"
        })
        break
      case "opponent-user":
      case "opponent-active":
      case "opponent-view":
        gsap.from(refContent.current, {
          transform: "rotateY(180deg)",
          rotate: "180deg",
          duration: 0.3,
          ease: "power2.inOut"
        })
        break
    }
  }, [])

  useGSAP(() => {
    if (owner !== "user" || !isInteractive) return

    const card = refCard.current

    if (!card) return

    let isAboveThreshold: boolean | null = null
    let isShowDiscardField: boolean | null = null

    const draggable = Draggable.create(card, {
      bounds: undefined,
      onPress() {
        hapticFeedback()

        isAboveThreshold = null
        isShowDiscardField = null

        gsap.set(card, {
          zIndex: 100
        })
      },

      onDrag() {
        if (!refIsMatchActive?.current) {
          return
        }

        refIsDraggable.current = true

        const activeCardField = document.getElementById(
          "game-desk-active-card-field"
        )
        const discardField = document.getElementById(
          "game-desk-discard-field-gradient"
        )

        const isHitActiveField = Draggable.hitTest(card, activeCardField, "50%")
        const isHitDiscardField = Draggable.hitTest(card, discardField, "70%")

        if (isHitActiveField !== isAboveThreshold) {
          isAboveThreshold = isHitActiveField

          if (isHitActiveField) {
            gsap.to(card, {
              scale: 1.2,
              duration: 0.3
            })
          } else {
            gsap.to(card, {
              scale: 1.6,
              duration: 0.3
            })
          }
        }

        if (isHitDiscardField !== isShowDiscardField) {
          isShowDiscardField = isHitDiscardField

          if (isHitDiscardField) {
            gsap.to(card, {
              scale: 1.2,
              duration: 0.3
            })
          } else {
            gsap.to(card, {
              scale: 1.6,
              duration: 0.3
            })
          }
        }

        const cardContentRect = card.getBoundingClientRect()
        const cardContentCenter =
          cardContentRect.left + cardContentRect.width / 2
        const pctFromRight =
          100 -
          ((window.innerWidth - cardContentCenter) / window.innerWidth) * 100

        const opacityGradient = Math.max(0.5, pctFromRight / 100)
        const opacityOverlay = Math.min(
          0.6,
          -0.6 + Math.max(0.5, pctFromRight / 100)
        )

        gsap.to("#game-desk-discard-field-gradient", {
          opacity: opacityGradient,
          duration: 0.5,
          ease: "power2.out"
        })
        gsap.to("#game-desk-discard-field-overlay", {
          opacity: opacityOverlay,
          duration: 0.5,
          ease: "power2.out"
        })
      },
      onDragEnd() {
        gsap.to(card, {
          x: 0,
          y: 0,
          scale: 1,
          duration: 0.3,
          onComplete: () => {
            gsap.set(card, { clearProps: "zIndex" })
          }
        })

        const activeCardField = document.getElementById(
          "game-desk-active-card-field"
        )
        const discardField = document.getElementById(
          "game-desk-discard-field-gradient"
        )
        const isHitActiveField = Draggable.hitTest(card, activeCardField, "50%")
        const isHitDiscardField = Draggable.hitTest(card, discardField, "70%")

        gsap.to(
          [
            "#game-desk-discard-field-gradient",
            "#game-desk-discard-field-overlay"
          ],
          {
            opacity: 0,
            duration: 0.5
          }
        )

        if (
          isHitActiveField &&
          refIsMyTurn?.current &&
          refIsAvailableToUse.current &&
          refIsMatchActive?.current
        ) {
          refOnActivateCard.current?.()
          hapticFeedback()
          return
        }

        if (
          isHitDiscardField &&
          refIsMyTurn?.current &&
          refIsMatchActive?.current
        ) {
          refOnClose.current?.()
          hapticFeedback()
          return
        }

        if (isHitActiveField || isHitDiscardField) {
          hapticFeedback({ type: "notificationOccurred", style: "warning" })
        }
      }
    })[0]

    return () => draggable.kill()
  }, [isInteractive, owner])

  return {
    refCard,
    refContent,
    onCardClick,
    onCardTouchEnd
  }
}
